import * as React from "react";
import ButtonPrimary from "../../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonTertiary from "../../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import {PARTNER_BRAND_TAB_LABELS, PartnerBrandTab} from "./PartnerBrandSections";

interface Props {
	/**
	 * Tabs holding unsaved changes, in page order. With none, there is nothing to update.
	 */
	dirtyTabs: PartnerBrandTab[];
	/**
	 * Number of fields with an error per tab, only for tabs that have any, in page order.
	 */
	errorTabs: Array<{tab: PartnerBrandTab, count: number}>;
	/**
	 * Opens the tab and moves to its first field with an error.
	 */
	onErrorTabClick: (tab: PartnerBrandTab) => void;
	/**
	 * Drops the unsaved changes, staying on the page.
	 */
	onDiscard: () => void;
	onSave: () => void;
	isSubmitting: boolean;
	/**
	 * Element the bar lines up with, left edge and width.
	 */
	alignTo: () => HTMLElement | null;
}

interface State {
	left: number;
	width: number;
}

/**
 * Bar floating above the footer with the save state of the whole page and its single save, so saving never needs
 * a scroll to the bottom. With nothing to save there is nothing to show, so it appears only with changes or errors.
 */
export default class PartnerBrandSaveBar extends React.PureComponent<Props, State> {
	resizeObserver: any = null;

	constructor(props: Props) {
		super(props);
		this.state = {left: 0, width: 0};
	}

	componentDidMount() {
		window.addEventListener("resize", this.align);
		this.align();
	}

	componentDidUpdate() {
		this.align();
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.align);
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}

	align = () => {
		const target = this.props.alignTo();
		// The card also changes width when the page's scrollbar comes or goes, which no window resize reports.
		const ResizeObserver = (window as any).ResizeObserver;
		if (target && ResizeObserver && !this.resizeObserver) {
			this.resizeObserver = new ResizeObserver(this.align);
			this.resizeObserver.observe(target);
		}
		if (target) {
			const rect = target.getBoundingClientRect();
			if (rect.left !== this.state.left || rect.width !== this.state.width) {
				this.setState({left: rect.left, width: rect.width});
			}
		}
	}

	render() {
		const props = this.props;
		const errorCount = props.errorTabs.reduce((sum, t) => sum + t.count, 0);
		const hasChanges = props.dirtyTabs.length > 0;
		if ((!hasChanges && !errorCount && !props.isSubmitting) || !this.state.width) {
			return null;
		}
		return (
			<div className={"c-pb-save-bar"} style={{left: this.state.left, width: this.state.width}}>
				<div className={"c-pb-save-bar_inner"}>
					<div className={`c-pb-save-bar_status${errorCount ? " is-error" : hasChanges ? " is-dirty" : ""}`}
						 role={"status"}>
						{errorCount > 0 ?
							<span>
								{`Not saved: ${errorCount} ${errorCount === 1 ? "field needs" : "fields need"} attention in `}
								{props.errorTabs.map((t, i) =>
									<React.Fragment key={t.tab}>
										{i > 0 && ", "}
										<button type={"button"} className={"c-pb-save-bar_link"} onClick={() => props.onErrorTabClick(t.tab)}>
											{PARTNER_BRAND_TAB_LABELS[t.tab]}
										</button>
									</React.Fragment>)}
							</span>
							: hasChanges &&
							<>
								<span className={"c-pb-dirty-dot"}/>
								<span>{`Unsaved changes in ${props.dirtyTabs.map(tab => PARTNER_BRAND_TAB_LABELS[tab]).join(", ")}`}</span>
							</>}
					</div>
					<ButtonTertiary onClick={props.onDiscard}
									label={"Discard"}
									disabled={props.isSubmitting}/>
					<ButtonPrimary onClick={props.onSave}
								   label={"Update"}
								   disabled={props.isSubmitting || !hasChanges}
								   isLoading={props.isSubmitting}/>
				</div>
			</div>
		);
	}
}
